import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Send, CheckCircle2, AlertTriangle, Globe, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { StatusPill } from "@/components/common";

function Metric({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 transition-colors hover:border-slate-700">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-400">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className="display mt-3 text-3xl font-bold">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/emails", { params: { direction: "outbound", limit: 6 } }).then((r) => setRecent(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6 fade-up">
      <div>
        <h1 className="display text-2xl font-bold">Overview</h1>
        <p className="mt-1 text-sm text-slate-400">Your sending performance over the last 14 days.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric testid="metric-sent" icon={Send} label="Emails sent" value={stats.total_sent.toLocaleString()} accent="text-cyan-400" sub="outbound total" />
        <Metric testid="metric-delivery" icon={CheckCircle2} label="MX acceptance rate" value={`${stats.delivery_rate}%`} accent="text-emerald-400" sub={`${stats.delivered} accepted by recipient server`} />
        <Metric testid="metric-bounce" icon={AlertTriangle} label="Bounce rate" value={`${stats.bounce_rate}%`} accent="text-red-400" sub={`${stats.bounced} bounced`} />
        <Metric testid="metric-domains" icon={Globe} label="Verified domains" value={`${stats.verified_domains}/${stats.domains}`} accent="text-emerald-400" sub={`${stats.received} received`} />
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="display text-lg font-semibold">Delivery volume</h2>
          <span className="mono text-xs text-slate-500">14 days</span>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={stats.series}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F2937" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#6B7280", fontSize: 11 }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0B0F17", border: "1px solid #1F2937", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#9CA3AF" }} />
              <Area type="monotone" dataKey="delivered" name="Accepted" stroke="#10B981" strokeWidth={2} fill="url(#g1)" />
              <Area type="monotone" dataKey="bounced" stroke="#EF4444" strokeWidth={2} fill="transparent" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/40">
        <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
          <h2 className="display text-lg font-semibold">Recent activity</h2>
          <Link to="/app/logs" className="inline-flex items-center gap-1 text-sm text-emerald-400 hover:underline">View all <ArrowUpRight className="h-3.5 w-3.5" /></Link>
        </div>
        <div className="divide-y divide-slate-800">
          {recent.length === 0 && <div className="px-5 py-8 text-center text-sm text-slate-500">No emails yet.</div>}
          {recent.map((e) => (
            <div key={e.id} className="flex items-center justify-between px-5 py-3 text-sm">
              <div className="min-w-0">
                <div className="truncate font-medium">{e.subject}</div>
                <div className="mono truncate text-xs text-slate-500">{e.to_addrs?.join(", ")}</div>
              </div>
              <StatusPill status={e.delivery_mode === "legacy" ? "legacy" : e.status} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
